import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import MESSAGES from "../../../../common/data/constants/messages";
import { ISurvey } from "../../../../common/data/dtos/survey";
import { ISurveyHeader } from "../../../../common/data/dtos/survey-header";
import { ISurveyQuestion } from "../../../../common/data/dtos/survey-question";
import Badge from "../../../../components/bootstrap/Badge";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardActions, CardBody, CardFooter, CardFooterLeft, CardHeader, CardLabel, CardTitle } from "../../../../components/bootstrap/Card";
import { MoreActions } from "../../../../components/extras/MoreActions";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import ThemeContext from "../../../../contexts/themeContext";
import { appMenu } from "../../../../menu";
import surveyHeaderService from "../../../../services/survey-header-service";
import surveyQuestionService from "../../../../services/survey-question-service";
import surveyService from "../../../../services/survey-service";
import LoadingPage from "../../loading/LoadingPage";
import { SurveyHeaderItemModal } from "../../survey-header-item/Common/SurveyHeaderItemModal";
import { SurveyHeaderModal } from "../../survey-header/Common/SurveyHeaderModal";
import { SurveyQuestionItemModal } from "../../survey-question-item/Common/SurveyQuestionItemModal";
import { SurveyQuestionModal } from "../../survey-question/Common/SurveyQuestionModal";

type TTabs = 'Cabeçalhos' | 'Questões';
interface ITabs {
    [key: string]: TTabs;
}

export const CommonSurveySettingsList = () => {

    const TABS: ITabs = {
        HEADERS: 'Cabeçalhos',
        QUESTIONS: 'Questões'
    };

    const { mobileDesign } = useContext(ThemeContext);
    const { id } = useParams();

    const [activeTab, setActiveTab] = useState<TTabs>(TABS.HEADERS);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [isModalHeaderOpened, setIsHeaderModalOpened] = useState(false);
    const [isModalHeaderItemOpened, setIsHeaderItemModalOpened] = useState(false);
    const [isModalQuestionOpened, setIsQuestionModalOpened] = useState(false);
    const [isModalQuestionItemOpened, setIsQuestionItemModalOpened] = useState(false);

    const [selectedSurvey, setSelectedSurvey] = useState<ISurvey | null>(null);
    const [selectedSurveyHeader, setSelectedSurveyHeader] = useState<ISurveyHeader | null>(null);
    const [selectedSurveyQuestion, setSelectedSurveyQuestion] = useState<ISurveyQuestion | null>(null);

    useEffect(() => {
        if (id) {
            fetchSurvey();
        }
    }, []);

    const fetchSurvey = async () => {
        setIsLoading(true);

        if (!id) {
            showWarning(MESSAGES.VALIDATION.PARAMETERS_ERROR_VALIDATION);
            setIsLoading(false);
        } else {

            const response = await surveyService.getOneSurvey(id);

            setIsLoading(false);

            if (response.error) {
                showWarning(response.error);
                return;
            }

            setSelectedSurvey(response.survey || null);
        }
    };

    const toggleModal = (setter: React.Dispatch<React.SetStateAction<boolean>>, isVisible: boolean) => {
        setter(isVisible);
    };

    const handleAddHeaderClick = () => {
        toggleModal(setIsHeaderModalOpened, true);
    };

    const handleAddQuestionClick = () => {
        toggleModal(setIsQuestionModalOpened, true);
    }

    const onHeaderModalClose = () => {
        toggleModal(setIsHeaderModalOpened, false);
        setSelectedSurveyHeader(null);
    };

    const onHeaderItemModalClose = () => {
        toggleModal(setIsHeaderItemModalOpened, false);
        setSelectedSurveyHeader(null);
    };

    const onQuestionModalClose = () => {
        toggleModal(setIsQuestionModalOpened, false);
        setSelectedSurveyQuestion(null);
    };

    const onQuestionItemModalClose = () => {
        toggleModal(setIsQuestionItemModalOpened, false);
        setSelectedSurveyQuestion(null);
    };

    const onHeaderAfterSubmit = async () => {
        await fetchSurvey();
        onHeaderModalClose();
    };

    const onHeaderItemAfterSubmit = async () => {
        await fetchSurvey();
        onHeaderItemModalClose();
    };

    const onQuestionAfterSubmit = async () => {
        await fetchSurvey();
        onQuestionModalClose();
    };

    const onQuestionItemAfterSubmit = async () => {
        await fetchSurvey();
        onQuestionItemModalClose();
    };

    const handleHeaderDeleteClick = async (surveyHeader: ISurveyHeader) => {
        if (!surveyHeader.id) {
            showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
            return;
        }

        setIsLoading(true);

        const response = await surveyHeaderService.deleteSurveyHeader(surveyHeader.id);

        setIsLoading(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        showSuccess(MESSAGES.ACTION.DELETED);
        await fetchSurvey();
    };

    const handleQuestionDeleteClick = async (surveyQuestion: ISurveyQuestion) => {
        if (!surveyQuestion.id) {
            showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
            return;
        }

        setIsLoading(true);

        const response = await surveyQuestionService.deleteSurveyQuestion(surveyQuestion.id);

        setIsLoading(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        showSuccess(MESSAGES.ACTION.DELETED);
        await fetchSurvey();
    };

    const handleHeaderEditClick = (surveyHeader: ISurveyHeader) => {
        setSelectedSurveyHeader(surveyHeader);
        toggleModal(setIsHeaderModalOpened, true);
    };

    const handleQuestionEditClick = (surveyQuestion: ISurveyQuestion) => {
        setSelectedSurveyQuestion(surveyQuestion);
        toggleModal(setIsQuestionModalOpened, true);
    };

    const handleSurveyHeaderItemsClick = (surveyHeader: ISurveyHeader) => {
        setSelectedSurveyHeader(surveyHeader);
        toggleModal(setIsHeaderItemModalOpened, true);
    }

    const handleSurveyQuestionItemsClick = (surveyQuestion: ISurveyQuestion) => {
        setSelectedSurveyQuestion(surveyQuestion);
        toggleModal(setIsQuestionItemModalOpened, true);
    }

    if (isLoading) {
        return <LoadingPage />;
    }

    return (
        <>
            <div className='row h-100'>

                <div className='col-xxl-3 col-xl-3 col-lg-4 col-md-4 col-sm-12'>
                    <Card stretch style={mobileDesign ? { minHeight: 200 } : { minHeight: 400 }}>
                        <CardHeader>
                            <CardLabel icon='Feed' iconColor='primary'>
                                <CardTitle tag='div' className='h5'>
                                    {selectedSurvey?.name}
                                </CardTitle>
                            </CardLabel>
                        </CardHeader>
                        <CardBody isScrollable>
                            <div className='row g-3'>
                                <div className='col-12'>
                                    <Button
                                        icon='ViewHeadline'
                                        color='primary'
                                        className='w-100 p-3'
                                        isLight={TABS.HEADERS !== activeTab}
                                        onClick={() => setActiveTab(TABS.HEADERS)}>
                                        {TABS.HEADERS}
                                    </Button>
                                </div>
                                <div className='col-12'>
                                    <Button
                                        icon='ViewList'
                                        color='primary'
                                        className='w-100 p-3'
                                        isLight={TABS.QUESTIONS !== activeTab}
                                        onClick={() => setActiveTab(TABS.QUESTIONS)}>
                                        {TABS.QUESTIONS}
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                        <CardFooter>
                            <CardFooterLeft className='w-100'>
                                <Button
                                    icon='ArrowBack'
                                    color='danger'
                                    isLight
                                    className='w-100 p-3'
                                    tag='a'
                                    to={`../${appMenu.registers.subMenu.surveys.path}`}
                                >
                                    Voltar
                                </Button>
                            </CardFooterLeft>
                        </CardFooter>
                    </Card>
                </div>

                <div className='col-xxl-9 col-xl-9 col-lg-8 col-md-8 col-sm-12'>

                    {TABS.HEADERS === activeTab && (
                        <>
                            <Card stretch style={{ minHeight: 300 }}>
                                <CardHeader borderSize={1}>
                                    <CardLabel icon='ViewHeadline' iconColor='primary'>
                                        <CardTitle tag='div' className='h5'>
                                            Cabeçalhos
                                        </CardTitle>
                                    </CardLabel>
                                    <CardActions>

                                        <Button
                                            color='light'
                                            icon='AddCircle'
                                            onClick={handleAddHeaderClick}
                                        >
                                            Adicionar
                                        </Button>
                                    </CardActions>
                                </CardHeader>
                                <CardBody isScrollable className='table-responsive'>

                                    <table className='table table-modern table-hover'>
                                        <thead>
                                            <tr>
                                                <th>Título</th>
                                                <th>Descrição</th>
                                                <th>Situação</th>
                                                <td aria-labelledby='Actions' />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedSurvey?.headers?.map((item: ISurveyHeader, index) => (
                                                <tr key={item.id}>
                                                    <td>{item.title || '-'}</td>
                                                    <td>{item.description}</td>
                                                    <td>
                                                        {item.status ? (
                                                            <h5>
                                                                <Badge color="success" className="align-items-center justify-content-center">
                                                                    Ativo
                                                                </Badge>
                                                            </h5>
                                                        ) : (
                                                            <h5>
                                                                <Badge color="danger" className="align-items-center justify-content-center">
                                                                    Inativo
                                                                </Badge>
                                                            </h5>
                                                        )}
                                                    </td>

                                                    <MoreActions
                                                        id={item.id}
                                                        showEdit={true}
                                                        showDelete={true}
                                                        onEditClick={() => handleHeaderEditClick(item)}
                                                        onDeleteClick={() => handleHeaderDeleteClick(item)}
                                                        customActions={[
                                                            {
                                                                label: 'Itens',
                                                                icon: 'ClearAll',
                                                                onClickAction: () => handleSurveyHeaderItemsClick(item),
                                                            }
                                                        ]}
                                                    />
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                </CardBody>
                            </Card>

                            <SurveyHeaderModal
                                isModalOpened={isModalHeaderOpened}
                                onModalClose={onHeaderModalClose}
                                onAfterSubmit={onHeaderAfterSubmit}
                                selectedSurvey={selectedSurvey || null}
                                selectedSurveyHeader={selectedSurveyHeader}
                            />

                            <SurveyHeaderItemModal
                                isModalOpened={isModalHeaderItemOpened}
                                onModalClose={onHeaderItemModalClose}
                                onAfterSubmit={onHeaderItemAfterSubmit}
                                selectedSurvey={selectedSurvey || null}
                                selectedSurveyHeader={selectedSurveyHeader}
                            />

                        </>
                    )}

                    {TABS.QUESTIONS === activeTab && (
                        <>
                            <Card stretch style={{ minHeight: 300 }}>
                                <CardHeader borderSize={1}>
                                    <CardLabel icon='ViewList' iconColor='primary'>
                                        <CardTitle tag='div' className='h5'>
                                            Questões
                                        </CardTitle>
                                    </CardLabel>
                                    <CardActions>

                                        <Button
                                            color='light'
                                            icon='AddCircle'
                                            onClick={handleAddQuestionClick}
                                        >
                                            Adicionar
                                        </Button>
                                    </CardActions>
                                </CardHeader>
                                <CardBody isScrollable className='table-responsive'>

                                    <table className='table table-modern table-hover'>
                                        <thead>
                                            <tr>
                                                <th>Título</th>
                                                <th>Situação</th>
                                                <td aria-labelledby='Actions' />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedSurvey?.questions?.map((item: ISurveyQuestion, index) => (
                                                <tr key={item.id}>
                                                    <td>{item.title || '-'}</td>
                                                    <td>
                                                        {item.status ? (
                                                            <h5>
                                                                <Badge color="success" className="align-items-center justify-content-center">
                                                                    Ativo
                                                                </Badge>
                                                            </h5>
                                                        ) : (
                                                            <h5>
                                                                <Badge color="danger" className="align-items-center justify-content-center">
                                                                    Inativo
                                                                </Badge>
                                                            </h5>
                                                        )}
                                                    </td>

                                                    <MoreActions
                                                        id={item.id}
                                                        showEdit={true}
                                                        showDelete={true}
                                                        onEditClick={() => handleQuestionEditClick(item)}
                                                        onDeleteClick={() => handleQuestionDeleteClick(item)}
                                                        customActions={[
                                                            {
                                                                label: 'Itens',
                                                                icon: 'ClearAll',
                                                                onClickAction: () => handleSurveyQuestionItemsClick(item),
                                                            }
                                                        ]}
                                                    />
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                </CardBody>
                            </Card>

                            <SurveyQuestionModal
                                isModalOpened={isModalQuestionOpened}
                                onModalClose={onQuestionModalClose}
                                onAfterSubmit={onQuestionAfterSubmit}
                                selectedSurvey={selectedSurvey || null}
                                selectedSurveyQuestion={selectedSurveyQuestion}
                            />

                            <SurveyQuestionItemModal 
                                isModalOpened={isModalQuestionItemOpened}
                                onModalClose={onQuestionItemModalClose}
                                onAfterSubmit={onQuestionItemAfterSubmit}
                                selectedSurvey={selectedSurvey || null}
                                selectedSurveyQuestion={selectedSurveyQuestion}
                            />
                        </>
                    )}

                </div>
            </div>
        </>
    );
}